import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";

const Row = ({ l, v }) => (
  <div className="flex justify-between text-sm py-1"><span className="text-gray-400 font-semibold">{l}</span><span className="font-bold text-right">{v ?? "—"}</span></div>
);

export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/admin/customers/${id}`).then((r) => setD(r.data)); }, [id]);
  if (!d) return <div className="p-10 text-center text-gray-400 font-bold">Загрузка...</div>;
  const c = d.customer;
  const delCustomer = async () => {
    if (!window.confirm("Удалить клиента из базы? Будут удалены его визиты и заявки. Действие необратимо.")) return;
    try {
      await api.delete(`/admin/customers/${c.id}`);
      toast.success("Клиент удалён");
      navigate("/admin/customers");
    } catch (e) { toast.error("Ошибка удаления"); }
  };
  return (
    <div className="space-y-4" data-testid="customer-profile">
      <div className="flex items-center gap-2">
        <Link to="/admin/customers" className="p-2 bg-white rounded-xl border border-gray-200"><ArrowLeft size={16} /></Link>
        <h1 className="font-display font-black text-xl">{c.first_name || "Гость"} {c.last_name || ""} <span className="text-sm text-gray-400">#{c.personal_code}</span></h1>
        <button data-testid="profile-delete-customer-btn" onClick={delCustomer}
          className="ml-auto admin-btn admin-btn-ghost text-xs !text-red-500"><Trash2 size={13} /> Удалить клиента</button>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <div className="text-sm font-extrabold mb-2">Профиль</div>
          <Row l="Телефон" v={c.phone} /><Row l="Email" v={c.email} />
          <Row l="Telegram" v={c.telegram_username ? `@${c.telegram_username}` : c.telegram_user_id} />
          <Row l="Пол" v={c.gender === "male" ? "Мужской" : c.gender === "female" ? "Женский" : "—"} />
          <Row l="Дата рождения" v={c.birthday} /><Row l="Возраст" v={c.age} /><Row l="Город" v={c.city} />
          <Row l="Язык" v={c.language?.toUpperCase()} />
          <Row l="Маркетинг-согласие" v={c.marketing_consent ? "✓ ON" : "OFF"} />
          <div className="mt-3 text-center">
            <img src={`${API}/qr/data?text=${c.personal_code}`} alt="" className="w-28 h-28 mx-auto rounded-xl border" />
            <div className="text-xs font-bold text-gray-400 mt-1">Персональный QR</div>
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-white rounded-2xl p-4 border border-gray-100">
            <div className="text-sm font-extrabold mb-2">Активность</div>
            <Row l="Регистрация" v={c.created_at?.slice(0, 10)} />
            <Row l="Последняя активность" v={c.last_activity?.slice(0, 10)} />
            <Row l="Посещений" v={c.total_visits} /><Row l="Покупок" v={c.total_purchases} />
            <Row l="Кампаний" v={(c.campaign_history || []).length} />
            <Row l="Wi-Fi сессий" v={(d.wifi_sessions || []).length} />
            <Row l="Рассылок получено" v={c.broadcast_count || 0} />
          </div>
          <div className="bg-white rounded-2xl p-4 border border-gray-100">
            <div className="text-sm font-extrabold mb-2">Loyalty</div>
            {(d.loyalty || []).length === 0 && <div className="text-xs text-gray-400">Не участвует</div>}
            {(d.loyalty || []).map((l) => (
              <div key={l.campaign_id} className="py-1.5">
                <div className="flex justify-between text-sm font-bold"><span>{l.name?.ru}</span><span>{l.count}/{l.required}</span></div>
                <div className="h-2 bg-gray-100 rounded-full mt-1"><div className="h-2 bg-[#FEBC10] rounded-full" style={{ width: `${Math.min(100, (l.count / l.required) * 100)}%` }} /></div>
                <div className="text-xs text-gray-400 mt-0.5">{l.reward_status === "available" ? "🎁 Награда доступна" : l.reward_status === "redeemed" ? "Награда использована" : "В процессе"}</div>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl p-4 border border-gray-100">
            <div className="text-sm font-extrabold mb-2">Custom Fields</div>
            {Object.entries(c.fields || {}).map(([k, v]) => <Row key={k} l={k} v={String(v.value)} />)}
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-white rounded-2xl p-4 border border-gray-100">
            <div className="text-sm font-extrabold mb-2">Campaign History</div>
            {(c.campaign_history || []).slice().reverse().slice(0, 10).map((h, i) => (
              <div key={i} className="flex justify-between text-xs py-1 border-b border-gray-50">
                <span className="font-bold">{h.campaign_name} <span className="text-gray-400">({h.type})</span></span>
                <span className="text-gray-400">{h.completed_at?.slice(0, 10)}</span>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl p-4 border border-gray-100">
            <div className="text-sm font-extrabold mb-2">Визиты / Покупки</div>
            {(d.visits || []).slice(0, 10).map((v) => (
              <div key={v.id} className="flex justify-between text-xs py-1 border-b border-gray-50">
                <span className="font-bold">{v.type === "visit" ? "Посещение" : "Покупка"} · {v.method === "campaign" ? (v.campaign_name || "Кампания") : (v.staff_name || "—")}</span>
                <span className="text-gray-400">{v.ts?.slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
            {(d.visits || []).length === 0 && <div className="text-xs text-gray-400">Нет записей</div>}
          </div>
          <div className="bg-white rounded-2xl p-4 border border-gray-100">
            <div className="text-sm font-extrabold mb-2">Промокоды</div>
            {(d.promos || []).map((p) => (
              <div key={p.id} className="flex justify-between text-xs py-1"><span className="font-bold">{p.code}</span><span className="text-gray-400">{p.issued_at?.slice(0, 10)}</span></div>
            ))}
            {(d.promos || []).length === 0 && <div className="text-xs text-gray-400">Нет промокодов</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
